import apiClient, { ApiResponse } from "./client";
import { config } from "../config";
import { notify } from "@/lib/utils/toast";

export interface Scholarship {
	id: string;
	year: string; // Năm học
	semester: string; // Học kỳ

	studentId: string; // Mã sinh viên
	studentName: string; // Họ tên
	dateOfBirth: string; // Ngày sinh
	cccd: string; // CCCD
	gender?: string;

	gpa: number; // Điểm TB
	conductScore: number; // Điểm RL
	major: string; // Ngành

	scholarshipCode: string; // Mã KT-CN
	scholarshipName: string; // Tên KT-CN
	title: string; // Danh hiệu
	amount: number; // Số tiền KT-CN
	certificateNo: string; // Số quyết định
	percentLevel: string; // Mức PT

	notes?: string; // Ghi chú (tùy chọn)
	createdAt?: string;
	updatedAt?: string;
}

export interface ScholarshipFilters {
	search?: string;
	title?: string;
	major?: string;
	createFrom?: string;
	createTo?: string;
	updateFrom?: string;
	updateTo?: string;
	page?: number;
	limit?: number;
}

export interface ScholarshipListResponse {
	data: Scholarship[];
	total: number;
	page: number;
	totalPages: number;
	limit: number;
}

export interface BulkImportResponse {
	created: Scholarship[];
	count: number;
	errors?: { row: number; message: string }[];
}

export const scholarshipApi = {
	// Get all bachelor degrees with optional filters
	getAll: async (filters?: ScholarshipFilters): Promise<ApiResponse<ScholarshipListResponse>> => {
		try {
			const response = await apiClient.get<ScholarshipListResponse>(config.endpoints.scholarships, filters);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error;
		}
	},

	getById: async (id: string, field?: string): Promise<ApiResponse<Scholarship>> => {
		try {
			const response = await apiClient.get<Scholarship>(`${config.endpoints.scholarships}/de/?id=${id}&field=${field}`);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	create: async (degree: Omit<Scholarship, "id" | "createdAt" | "updatedAt">): Promise<ApiResponse<Scholarship>> => {
		try {
			const response = await apiClient.post<Scholarship>(config.endpoints.scholarships, degree);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	update: async (id: string, degree: Partial<Scholarship>): Promise<ApiResponse<Scholarship>> => {
		try {
			const response = await apiClient.put<Scholarship>(`${config.endpoints.scholarships}/${id}`, degree);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	delete: async (id: string) => {
		try {
			await apiClient.delete<void>(`${config.endpoints.scholarships}/${id}`);
			notify.success("Xóa Khen thưởng - Chứng nhận thành công");
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	bulkImport: async (degrees: Omit<Scholarship, "id" | "createdAt" | "updatedAt">[]): Promise<ApiResponse<BulkImportResponse>> => {
		try {
			const response = await apiClient.post<BulkImportResponse>(`${config.endpoints.scholarships}/bulk-import`, degrees);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	export: async (filters?: ScholarshipFilters): Promise<ApiResponse<Scholarship[]>> => {
		try {
			const response = await apiClient.get<Scholarship[]>(`${config.endpoints.scholarships}/export`, filters);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	// Search public bachelor degrees (for public search page)
	searchPublic: async (query: { cccd?: string; studentId?: string; degreeNumber?: string }): Promise<ApiResponse<Scholarship[]>> => {
		try {
			const response = await apiClient.get<Scholarship[]>(`${config.endpoints.public.search}/bachelor-degrees`, query);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			throw error
		}
	},

	checkExist: async ({ certificateNo }: { certificateNo: string }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.scholarships}/check-exist`, { certificate_number: certificateNo });
		return response;
	},
};
